<div cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragHandle>
  <app-modal-header-ui [title]="title" [showCloseBtn]="false"></app-modal-header-ui>

  <div class="dialog-body">
    <mat-progress-bar *ngIf="loadingPolicies" mode="indeterminate"></mat-progress-bar>
    <div *ngIf="loadError" class="balance-warning" role="alert">
      <!-- <mat-icon>error_outline</mat-icon> -->
      <span>{{ loadError }}</span>
      <button type="button" mat-button color="primary" (click)="loadPolicies()" i18n="@@retry">Retry</button>
    </div>

    <form [formGroup]="leaveForm" (ngSubmit)="submit()" class="leave-form">

      <!-- Leave Type Selector -->
      <mat-form-field class="w-full leave-category-field" appearance="outline">
        <mat-label i18n="@@leaveCategory">Leave Category</mat-label>
        <mat-select formControlName="policyId">
          <mat-option *ngFor="let policy of policies" [value]="policy.policyId">
            {{ policyLabel(policy) }}
          </mat-option>
        </mat-select>
        <mat-hint *ngIf="noDaysLeft" class="no-days-left">{{ noDaysLeftMessage }}</mat-hint>
        <mat-hint *ngIf="!noDaysLeft && selectedPolicy?.minNoticePeriodDays">
          <ng-container i18n="@@minNoticeHint">Minimum notice (days):</ng-container> {{ selectedPolicy?.minNoticePeriodDays }}
        </mat-hint>
        <mat-hint align="end" *ngIf="isMe && selectedPolicy as policy">
          {{ policy.remaining }} <ng-container i18n="@@remainingLower">remaining</ng-container>,
          {{ policy.available }} <ng-container i18n="@@availableLower">available</ng-container>
        </mat-hint>
        <mat-error *ngIf="leaveForm.get('policyId')?.hasError('required')" i18n="@@leaveCategoryRequired">
          Leave category is required
        </mat-error>
        <mat-error *ngIf="fieldError('policyId') as msg">{{ msg }}</mat-error>
      </mat-form-field>

      <!-- Date Range -->
      <div class="date-row">
        <mat-form-field class="date-field" appearance="outline">
          <mat-label i18n="@@startDate">Start Date</mat-label>
          <input matInput [matDatepicker]="startPicker" formControlName="startDate" [min]="minDate">
          <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
          <mat-datepicker #startPicker></mat-datepicker>
          <mat-error *ngIf="leaveForm.get('startDate')?.hasError('required')" i18n="@@startDateRequired">
            Start date is required
          </mat-error>
          <mat-error *ngIf="fieldError('startDate') as msg">{{ msg }}</mat-error>
        </mat-form-field>

        <mat-form-field class="date-field" appearance="outline">
          <mat-label i18n="@@endDate">End Date</mat-label>
          <input matInput [matDatepicker]="endPicker" formControlName="endDate" [min]="leaveForm.get('startDate')?.value || minDate">
          <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
          <mat-datepicker #endPicker></mat-datepicker>
          <mat-error *ngIf="leaveForm.get('endDate')?.hasError('required')" i18n="@@endDateRequired">
            End date is required
          </mat-error>
          <mat-error *ngIf="leaveForm.get('endDate')?.hasError('invalidEndDate')" i18n="@@invalidEndDate">
            End date must be after start date
          </mat-error>
          <mat-error *ngIf="fieldError('endDate') as msg">{{ msg }}</mat-error>
        </mat-form-field>
      </div>

      <!-- Calculated working days (once both dates are chosen) -->
      <div
        class="working-days-display"
        *ngIf="calculatedWorkingDays !== null && !noDaysLeft"
        [class.valid]="!exceedsAvailable"
        [class.invalid]="exceedsAvailable"
        role="status"
      >
        <mat-icon>{{ exceedsAvailable ? 'warning' : 'event_available' }}</mat-icon>
        <span>
          <ng-container i18n="@@calculatedWorkingDays">Calculated working days</ng-container>:
          <strong>{{ calculatedWorkingDays }}</strong>
          <span class="hint" *ngIf="exceedsAvailable">
            (<ng-container i18n="@@exceedsAvailableDays">more than your available days</ng-container>: {{ selectedPolicy?.available }})
          </span>
        </span>
      </div>

      <!-- Relieving officer -->
      <mat-form-field class="w-full" appearance="outline">
        <mat-label i18n="@@relievingOfficer">Relieving Officer</mat-label>
        <mat-select formControlName="relievingOfficerId">
          <mat-option>
            <ngx-mat-select-search
              [placeholderLabel]="searchPlaceholder"
              [noEntriesFoundLabel]="noStaffLabel"
              [searching]="loadingOfficers"
              [(ngModel)]="officerSearch"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onOfficerSearch($event)"
            ></ngx-mat-select-search>
          </mat-option>
          <mat-option [value]="null" *ngIf="!requiresReliever">-</mat-option>
          <mat-option *ngFor="let officer of officers" [value]="officer.id">{{ officer.name }}</mat-option>
        </mat-select>
        <mat-hint *ngIf="requiresReliever" i18n="@@relieverRequired">Required for this leave type</mat-hint>
        <mat-error *ngIf="leaveForm.get('relievingOfficerId')?.hasError('required')" i18n="@@relieverRequiredError">
          Relieving officer is required
        </mat-error>
        <mat-error *ngIf="fieldError('relievingOfficerId') as msg">{{ msg }}</mat-error>
      </mat-form-field>

      <!-- Designated approver: staff on a higher salary grade. Always shown; disabled with a reason when there is nobody to pick. -->
      <mat-form-field class="w-full" appearance="outline">
        <mat-label i18n="@@designatedApprover">Designated Approver</mat-label>
        <mat-select formControlName="reviewerId" [required]="reviewerNeeded">
          <mat-option>
            <ngx-mat-select-search
              [placeholderLabel]="searchPlaceholder"
              [noEntriesFoundLabel]="noStaffLabel"
              [searching]="loadingReviewers"
              [(ngModel)]="reviewerSearch"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onReviewerSearch($event)"
            ></ngx-mat-select-search>
          </mat-option>
          <mat-option *ngFor="let reviewer of reviewers" [value]="reviewer.id">{{ reviewer.name }}</mat-option>
        </mat-select>
        <mat-hint *ngIf="reviewerHint">
          {{ reviewerHint }}
          <a *ngIf="reviewerStatus === 'failed'" class="reviewer-retry" role="button" tabindex="0" (click)="retryReviewers()" (keydown.enter)="retryReviewers()" i18n="@@retry">Retry</a>
        </mat-hint>
        <mat-error *ngIf="leaveForm.get('reviewerId')?.hasError('required')" i18n="@@reviewerRequiredError">
          Choose who will review this leave request
        </mat-error>
        <mat-error *ngIf="fieldError('reviewerId') as msg">{{ msg }}</mat-error>
      </mat-form-field>

      <!-- How long a request on this leave type stays valid (not the days the staff is entitled to) -->
      <p class="leave-validity" *ngIf="selectedPolicy?.validityDays as days">
        <span class="validity-label" i18n="@@leaveValidity">Leave valid for</span>
        <strong>{{ days }} <ng-container i18n="@@days">days</ng-container></strong>
      </p>

      <!-- Reason -->
      <mat-form-field class="w-full reason-field" appearance="outline">
        <mat-label i18n="@@reason">Reason</mat-label>
        <input matInput formControlName="reason" maxlength="500">
        <mat-error *ngIf="fieldError('reason') as msg">{{ msg }}</mat-error>
      </mat-form-field>

      <!-- Backend business-rule error (insufficient balance, notice period, overlap...) -->
      <div *ngIf="errorMessage" class="balance-warning" role="alert">
        <mat-icon>warning</mat-icon>
        <span>{{ errorMessage }}</span>
      </div>

      <!-- Actions -->
      <div class="dialog-actions">
        <span
          class="disabled-action-tooltip"
          [matTooltip]="submitDisabledReason"
          [matTooltipDisabled]="!submitDisabledReason"
          matTooltipPosition="above"
        >
          <app-button-icon-ui
            [buttonType]="'submit'"
            [isDisabled]="submitDisabled"
            classNames="p-3 py-4 rounded-xl text-white bg-success/90 hover:bg-success w-[150px]"
            i18n-name="@@submit"
            name="Submit"
            value="submit"
          ></app-button-icon-ui>
        </span>
        <app-button-icon-ui
          [buttonType]="'button'"
          classNames="p-3 py-4 rounded-xl border border-error opacity-80 hover:opacity-100 text-error w-[150px]"
          i18n-name="@@cancel"
          (clickEmit)="cancel()"
          name="Cancel"
          value="cancel"
        ></app-button-icon-ui>
      </div>
    </form>
  </div>
</div>
